/* eslint-disable jira/filename-case */

import OriginTracing from '@atlassiansox/origin-tracing/origin-tracer';
import { resolveAnalyticsWebClientPromise } from '@jira/platform__observability-product-analytics-web-client-async';
import type {
	InitAttrs,
	AnalyticsWebClient,
} from '@jira/platform__observability-product-analytics-web-client-types';
import DefaultAnalyticsWebClient from './utils/default-analytics-web-client.tsx';
import SSRAnalyticsWebClient from './utils/ssr-analytics-web-client.tsx';
/**
 * Sets up the analytics web client in the web environment, ensuring it's ready for event tracking. This includes initializing the client if it's not already set up and configuring origin tracing to enrich analytics events.
 */
export const initAnalyticsWebClient = (attrs: InitAttrs): boolean | undefined => {
	let internalClientInitialised: boolean | undefined;

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	if (!window.analyticsWebClient) {
		try {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.analyticsWebClient = __SERVER__
				? SSRAnalyticsWebClient
				: new DefaultAnalyticsWebClient();

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			internalClientInitialised = window.analyticsWebClient.init(attrs);

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.analyticsWebClient.setOriginTracingHandlers({
				// oxlint-disable-next-line typescript/ban-ts-comment
				// @ts-expect-error
				atlOrigin: (encodedOrigin) => {
					const origin = OriginTracing.fromEncoded(encodedOrigin);
					const attributes = origin.toAnalyticsAttributes();
					return {
						originTracingAttributes: {
							id: 'originId' in attributes ? attributes.originId : undefined,
							product: 'originProduct' in attributes ? attributes.originProduct : undefined,
							library: attributes.originLibrary,
						},
						taskSessionId: origin.id ?? undefined,
					};
				},
			});
		} catch (err: unknown) {
			if (typeof window !== 'undefined') {
				window.reportError(new Error('Failed to initialize analytics client', { cause: err }));
			}
		}
	} else {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		internalClientInitialised = !!window.analyticsWebClient.getInstance();
	}

	// oxlint-disable-next-line typescript/no-non-null-assertion
	resolveAnalyticsWebClientPromise(window.analyticsWebClient!); // eslint-disable-line jira/jira-ssr/no-unchecked-globals-usage
	return internalClientInitialised;
};
// oxlint-disable-next-line typescript/no-non-null-assertion
export const getAnalyticsWebClient = (): AnalyticsWebClient => window.analyticsWebClient!; // eslint-disable-line jira/jira-ssr/no-unchecked-globals-usage
